import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';

const ChangeStatusDialog = ({ 
  open, 
  onOpenChange, 
  currentStatus, 
  availableStatuses, 
  onConfirm,
  itemType = 'quote' // quote, order, job
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [reason, setReason] = useState('');
  const [showWarning, setShowWarning] = useState(false);

  const statusConfig = {
    // Quote statuses
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: '📝' },
    sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '✉️' },
    accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700 border-green-300', icon: '✅' },
    declined: { label: 'Declined', color: 'bg-red-100 text-red-700 border-red-300', icon: '❌' },
    archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: '🗄️' },
    
    // Order statuses
    active: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-300', icon: '✅' },
    on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '⏸️' },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-300', icon: '❌' },
    
    // Job statuses
    unscheduled: { label: 'Unscheduled', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: '📋' },
    scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '📅' },
    in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: '🔧' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-300', icon: '✅' },
    invoiced: { label: 'Invoiced', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: '💰' },
    paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: '💳' }
  };

  const getWarningMessage = () => {
    const from = currentStatus;
    const to = selectedStatus;

    // Major status changes that need warnings
    if (from === 'accepted' && to === 'declined') {
      return 'This quote was accepted. If an order was created, you may need to cancel it separately.';
    }
    if (from === 'accepted' && to === 'draft') {
      return 'This will revert the quote to draft status. Any related orders will need to be managed separately.';
    }
    if (from === 'completed' && (to === 'in_progress' || to === 'scheduled')) {
      return 'This will reopen the job. If invoiced, you may need to void the invoice separately.';
    }
    if (from === 'invoiced' && to !== 'paid') {
      return 'This job has been invoiced. Consider voiding the invoice before changing status.';
    }
    if (from === 'paid' && to !== 'invoiced') {
      return 'Payment has been recorded for this job. Are you sure you want to change the status?';
    }
    
    return null;
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    const warning = getWarningMessage();
    setShowWarning(!!warning);
  };

  const handleConfirm = () => {
    onConfirm(selectedStatus, reason);
    setReason('');
    setSelectedStatus(currentStatus);
    setShowWarning(false);
  };

  const handleCancel = () => {
    setReason('');
    setSelectedStatus(currentStatus);
    setShowWarning(false);
    onOpenChange(false);
  };

  const warningMessage = getWarningMessage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Change Status</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">Current Status:</span>
            <Badge className={`${statusConfig[currentStatus]?.color} border`}>
              <span className="mr-1">{statusConfig[currentStatus]?.icon}</span>
              {statusConfig[currentStatus]?.label}
            </Badge>
          </div>

          {/* Status Options */}
          <div>
            <Label className="mb-3 block">Select New Status:</Label>
            <div className="grid grid-cols-2 gap-3">
              {availableStatuses.map(status => {
                const config = statusConfig[status];
                const isSelected = selectedStatus === status;
                const isCurrent = currentStatus === status;
                
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusSelect(status)}
                    disabled={isCurrent}
                    className={`
                      p-4 rounded-lg border-2 text-left transition-all
                      ${isCurrent ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}
                      ${isSelected ? config.color + ' border-current shadow-md scale-105' : 'bg-white border-gray-200'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{config.icon}</span>
                      <span className="font-medium">{config.label}</span>
                    </div>
                    {isCurrent && (
                      <span className="text-xs text-gray-500 mt-1 block">Current status</span>
                    )}
                    {isSelected && !isCurrent && (
                      <CheckCircle className="w-4 h-4 absolute top-2 right-2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Warning Message */}
          {showWarning && warningMessage && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">⚠️ Warning</p>
                <p className="text-sm text-amber-800">{warningMessage}</p>
              </div>
            </div>
          )}

          {/* Reason (optional) */}
          {selectedStatus !== currentStatus && (
            <div>
              <Label htmlFor="reason" className="mb-2 block">
                Reason for Change (optional)
                <span className="text-gray-500 font-normal ml-2">Helpful for tracking</span>
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Customer changed mind, Error in original quote, etc."
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={selectedStatus === currentStatus}
          >
            Confirm Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeStatusDialog;
